'use client';
import { refreshTenantRegistryToken } from '@/api/auth-client';
import { getAccessToken, getHttpSessionEpoch, setAccessToken, setTenantOverride, tokenHasCrossTenantPermission } from '@/api/http-session';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSharedPage } from '@/api/generated/share-external-link/share-external-link';

import { isPlayground } from '@/utils/check-is-playground';
import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useSearchParams } from 'next/navigation';
import { useSessionState } from './useSessionState';
import { ALL_LOCAL_FILTERS_KEYS, SHARED_FILTERS_MAP } from '@/constants/route-params-keys';
import { HIDDEN_FILTERS_STORE_KEY, SHARED_FILTERS_STORE_KEY } from '@/contexts/filters/filter-context';
import { SharedPageResponse } from '@/api/generated/model';
import { Duration } from '@/lib/duration';

export type AuthContextState = ReturnType<typeof useAuth>;

export const AuthContext = React.createContext<AuthContextState | null>(null);

export const AuthProvider = ({ children, isAuth0 }: { children: React.ReactNode; isAuth0: boolean }) => {
  const auth = useAuth({ isAuth0 });
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export type Permission =
  | 'read:*'
  | 'write:*'
  | 'admin:*'
  | 'delete:*'
  | 'share:*'
  | 'share:report'
  | 'share:feedback'
  | 'share:user_intelligence'
  | 'share:interaction_detail'
  | 'share:allow_filters'
  | 'share:edit_filters'
  | 'share:engagement'
  | 'share:edit_chart'
  | 'share:interactions'
  | 'view:llm_problems';

export type PermissionLabel = 'Read only' | 'Read only and see details' | 'Edit only' | 'Edit and see details';

type AuthDecoded = {
  tenant: string;
  roles?: string[];
  'nebuly/roles'?: string[];
  permissions: Permission[];
  email: string;
  full_name?: string;
  name?: string;
};

function resolveFullName(decoded: AuthDecoded | null): string | undefined {
  const fullName = decoded?.full_name?.trim();
  if (fullName) return fullName;
  const name = decoded?.name?.trim();
  if (name) return name;
  return undefined;
}

export function labelMapPermissions(permissions: Permission[]): PermissionLabel {
  const isEdit = permissions.some((s) => ['share:interaction_detail', 'share:edit_chart'].includes(s));

  if ('share:edit_filters' in permissions) {
    if (isEdit) {
      return 'Edit and see details';
    } else {
      return 'Edit only';
    }
  } else {
    if (isEdit) {
      return 'Read only and see details';
    } else {
      return 'Read only';
    }
  }
}

export const colorMapPermissions: Record<PermissionLabel, string> = {
  'Read only': 'rgba(146,76,236,0.15)',
  'Edit and see details': 'rgba(29,158,143,0.3)',
  'Edit only': 'rgba(234,79,98,0.3)',
  'Read only and see details': 'rgba(214,236,76,0.3)',
};

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

type TokenSource = 'auth0' | 'tenant-registry' | 'fixed';

const TOKEN_REFRESH_SKEW_MS = Duration.from({ minutes: 1 }).toMillis();

function persistAccessToken(
  token: string | null,
  setJwt: (token: string | null) => void,
  epoch = getHttpSessionEpoch(),
): void {
  if (epoch !== getHttpSessionEpoch()) return;
  setAccessToken(token, epoch);
  setJwt(token);
  // X-Tenant is impersonation-only. Sending it without crosstenant:* 403s every request.
  if (!tokenHasCrossTenantPermission(token)) {
    setTenantOverride(null);
  }
}

function isJwtExpired(token: string, skewMs = TOKEN_REFRESH_SKEW_MS): boolean {
  const decoded = jwtDecode<JwtPayload>(token);
  return !decoded.exp || Date.now() >= decoded.exp * 1000 - skewMs;
}

function msUntilRefresh(token: string): number {
  const decoded = jwtDecode<JwtPayload>(token);
  if (!decoded.exp) return TOKEN_REFRESH_SKEW_MS;
  return Math.max(decoded.exp * 1000 - Date.now() - TOKEN_REFRESH_SKEW_MS, 0);
}

function isTenantRegistryAccessToken(token: string): boolean {
  try {
    const { aud } = jwtDecode<{ aud?: string | string[] }>(token);
    const audiences = Array.isArray(aud) ? aud : aud ? [aud] : [];
    return audiences.includes('nebuly:access');
  } catch {
    return false;
  }
}

function dropMismatchedStoredToken(isAuth0: boolean, setJwt: (token: string | null) => void): void {
  const token = getAccessToken();
  if (!token) return;
  if (isAuth0 === isTenantRegistryAccessToken(token)) {
    persistAccessToken(null, setJwt);
  }
}

async function obtainAccessToken(
  isAuth0: boolean,
  getAuth0Token: (options?: GetTokenSilentlyOptions) => Promise<string>,
  forceRefresh = false,
): Promise<string | null> {
  if (isAuth0) {
    return getAuth0Token(forceRefresh ? { cacheMode: 'off' } : undefined);
  }
  return refreshTenantRegistryToken();
}

export function useAuth({ isAuth0 }: { isAuth0: boolean }) {
  const {
    user: auth0User,
    getAccessTokenSilently,
    isLoading: isAuth0Loading,
    isAuthenticated: isAuth0Authenticated,
  } = useAuth0();
  const [jwt, setJwt] = useState<string | null>(null);

  const decoded = useMemo(() => {
    if (isPlayground())
      return {
        tenant: 'nebuly_playground',
        roles: ['viewer'],
        'nebuly/roles': ['viewer'],
        permissions: ['read:*'] as Permission[],
        email: 'playground@nebuly.ai',
        full_name: 'Playground User',
      };
    if (auth0User) {
      return auth0User as AuthDecoded;
    }
    if (!jwt) return null;
    return jwtDecode<AuthDecoded & JwtPayload>(jwt);
  }, [jwt, auth0User]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown | null>(null);
  const tokenSourceRef = useRef<TokenSource | null>(null);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const [asTenant, setAsTenant] = useSessionState<string | null>('x-tenant', null);

  const authenticated = jwt !== null;

  const searchParams = useSearchParams();

  // old shared page
  const [queryToken] = useState(searchParams.get('token') || null);
  // new shared page
  const [sharedPageId] = useState(searchParams.get('sharedPageId') || null);
  const filterSetRef = useRef(false);

  useEffect(() => {
    //This is a workaround to restore filters from the query params
    if (queryToken && !sharedPageId && !filterSetRef.current) {
      filterSetRef.current = true;
      restoreFilterState(searchParams);
    }
  }, [queryToken, sharedPageId, searchParams]);

  const hasPermission = useCallback(
    (...permissions: Permission[]) => {
      if (isPlayground()) return true;
      if (!decoded) return false;
      if (isAuth0) return true;
      const result = permissions.some((p) => decoded.permissions?.includes(p));
      return result;
    },
    [decoded, isAuth0],
  );

  const hasRole = useCallback(
    (...roles: string[]) => {
      if (isPlayground()) return true;
      if (!decoded) return false;
      const userRoles = decoded.roles ?? decoded['nebuly/roles'] ?? [];
      return roles.some((r) => userRoles.map((e) => e.toLowerCase()).includes(r.toLowerCase()));
    },
    [decoded],
  );

  const roles = decoded?.roles ?? decoded?.['nebuly/roles'];
  const fullName = resolveFullName(decoded);
  const isNebulyAdmin = roles?.map((e) => e.toLowerCase()).includes('nebulyadmin') || false;
  const isProjectAdmin = roles?.map((e) => e.toLowerCase()).includes('projectadmin') || false;
  const isCompanyAdmin = roles?.map((e) => e.toLowerCase()).includes('admin') || isNebulyAdmin || isProjectAdmin;
  const canWrite = hasPermission('write:*');
  // Auth0: hasPermission always returns true (legacy bypass), so Viewer appears
  // to have write:*. Use role membership for report create/mutate UI instead.
  const canWriteReports =
    isPlayground() ||
    (roles?.some((r) => ['member', 'admin', 'projectadmin', 'nebulyadmin'].includes(r.toLowerCase())) ?? false);

  const tenant = useMemo(() => {
    return asTenant || decoded?.tenant;
  }, [asTenant, decoded]);

  const refreshAccessToken = useCallback(
    async (forceRefresh = false) => {
      const source = tokenSourceRef.current;
      if (source !== 'auth0' && source !== 'tenant-registry') return null;
      if (refreshPromiseRef.current) return refreshPromiseRef.current;

      const promise = (async () => {
        const epoch = getHttpSessionEpoch();
        try {
          const token = await obtainAccessToken(source === 'auth0', getAccessTokenSilently, forceRefresh);
          if (token) persistAccessToken(token, setJwt, epoch);
          return token;
        } catch (e) {
          setError(e);
          return null;
        } finally {
          refreshPromiseRef.current = null;
        }
      })();

      refreshPromiseRef.current = promise;
      return promise;
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (isPlayground()) {
      persistAccessToken(null, setJwt);
      tokenSourceRef.current = null;
      setLoading(false);
      return;
    }

    if (queryToken && !sharedPageId) {
      tokenSourceRef.current = 'fixed';
      persistAccessToken(queryToken, setJwt);
      setLoading(false);
      return;
    }

    if (sharedPageId) {
      tokenSourceRef.current = 'fixed';
      const epoch = getHttpSessionEpoch();
      persistAccessToken(queryToken, setJwt, epoch);
      getTokenAndFiltersFromSharedPageId(sharedPageId, (response) => {
        persistAccessToken(response.token, setJwt, epoch);
        const showFilters = response.permissions.includes('share:edit_filters');
        const store = showFilters ? SHARED_FILTERS_STORE_KEY : HIDDEN_FILTERS_STORE_KEY;
        localStorage.removeItem(SHARED_FILTERS_STORE_KEY);
        localStorage.removeItem(HIDDEN_FILTERS_STORE_KEY);
        localStorage.setItem(store, JSON.stringify(response.filter_store));
        setLoading(false);
      });
      return;
    }

    tokenSourceRef.current = isAuth0 ? 'auth0' : 'tenant-registry';
    dropMismatchedStoredToken(isAuth0, setJwt);

    if (isAuth0 && isAuth0Loading) return;

    if (isAuth0 && !isAuth0Authenticated) {
      persistAccessToken(null, setJwt);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const epoch = getHttpSessionEpoch();
    obtainAccessToken(isAuth0, getAccessTokenSilently, true)
      .then((token) => {
        if (cancelled) return;
        if (token) persistAccessToken(token, setJwt, epoch);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuth0, isAuth0Authenticated, isAuth0Loading, queryToken, sharedPageId]);

  useEffect(() => {
    if (!jwt || tokenSourceRef.current === 'fixed' || tokenSourceRef.current === null) return;

    const timeoutId = window.setTimeout(() => {
      void refreshAccessToken();
    }, msUntilRefresh(jwt));

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (isJwtExpired(jwt)) void refreshAccessToken();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [jwt, refreshAccessToken]);

  const forceRefreshToken = useCallback(async () => {
    return refreshAccessToken(true);
  }, [refreshAccessToken]);

  return useMemo(() => {
    return {
      // is authenticated?
      auth: authenticated,
      loading,
      error,
      isNebulyAdmin,
      isCompanyAdmin,
      isProjectAdmin,
      canWrite,
      canWriteReports,
      asTenant,
      setAsTenant,
      jwt,
      isSharedLink: isPlayground() ? searchParams.get('shared') === 'true' : !!queryToken || !!sharedPageId,
      hasPermission,
      hasRole,
      forceRefreshToken,
      tenant,
      email: decoded?.email,
      fullName,
    };
  }, [
    authenticated,
    loading,
    error,
    isNebulyAdmin,
    isCompanyAdmin,
    isProjectAdmin,
    canWrite,
    canWriteReports,
    asTenant,
    setAsTenant,
    sharedPageId,
    jwt,
    searchParams,
    queryToken,
    hasPermission,
    hasRole,
    forceRefreshToken,
    tenant,
    decoded?.email,
    fullName,
  ]);
}

async function getTokenAndFiltersFromSharedPageId(
  sharedPageId: string,
  onSuccess: (response: SharedPageResponse) => void,
) {
  try {
    const response = await getSharedPage(sharedPageId);
    onSuccess(response);
  } catch (e) {
    console.error('error getting shared page', e);
  }
}

function restoreFilterState(searchParams: URLSearchParams) {
  try {
    const filtersStore = JSON.parse(localStorage.getItem(SHARED_FILTERS_STORE_KEY) || '{}');

    ALL_LOCAL_FILTERS_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        try {
          filtersStore[key] = JSON.parse(value);
        } catch (e) {
          console.error('error restoring filters from the query params', e);
        }
      }
    });
    Object.entries(SHARED_FILTERS_MAP).forEach(([key, newKeys]) => {
      const value = searchParams.get(key);
      if (value) {
        try {
          const value = searchParams.get(key);
          newKeys.forEach((newKey) => {
            filtersStore[newKey] = value;
          });
        } catch (e) {
          console.error('error restoring filters from the query params', e);
        }
      }
    });
    localStorage.setItem(SHARED_FILTERS_STORE_KEY, JSON.stringify(filtersStore));
  } catch (e) {
    console.error('error reading filters from the local storage', e);
  }
}
